import type { CanonicalEntity } from '@/hooks/useEntities';

export function normalizeEntityNameKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Resolve a chronology / navigation name to at most one entity.
 *
 * Prefer an exact unique display-name match. If none, fall back to a unique
 * alias match. Shared names and aliases attach to nobody.
 */
export function resolveEntityByName(
  entities: readonly CanonicalEntity[],
  rawName: string,
): CanonicalEntity | null {
  const key = normalizeEntityNameKey(rawName);
  if (!key) return null;

  const displayMatches = entities.filter(
    entity => normalizeEntityNameKey(entity.name) === key,
  );
  if (displayMatches.length === 1) return displayMatches[0];
  if (displayMatches.length > 1) return null;

  const aliasMatches = entities.filter(entity =>
    entity.aliases.some(alias => normalizeEntityNameKey(alias) === key),
  );
  if (aliasMatches.length === 1) return aliasMatches[0];
  return null;
}

/**
 * Map each normalized name to the single entity id it uniquely identifies.
 * Shared display names and shared aliases are omitted.
 */
export function buildUniqueEntityNameIndex(
  entities: readonly CanonicalEntity[],
): Map<string, string> {
  const displayOwners = new Map<string, string[]>();
  const aliasOwners = new Map<string, string[]>();

  for (const entity of entities) {
    const displayKey = normalizeEntityNameKey(entity.name);
    if (displayKey) {
      const ids = displayOwners.get(displayKey) ?? [];
      ids.push(entity.id);
      displayOwners.set(displayKey, ids);
    }
    for (const alias of entity.aliases) {
      const aliasKey = normalizeEntityNameKey(alias);
      if (!aliasKey) continue;
      const ids = aliasOwners.get(aliasKey) ?? [];
      // Case variants of the same alias must not look like two owners.
      if (!ids.includes(entity.id)) ids.push(entity.id);
      aliasOwners.set(aliasKey, ids);
    }
  }

  const index = new Map<string, string>();
  for (const [key, ids] of displayOwners) {
    if (ids.length === 1) index.set(key, ids[0]);
  }
  for (const [key, ids] of aliasOwners) {
    // Shared or unique display names already decided ownership; do not let a
    // single alias claim a key that resolveEntityByName would leave unmatched.
    if (displayOwners.has(key)) continue;
    if (ids.length === 1) index.set(key, ids[0]);
  }
  return index;
}
